/*
  vitelli-pdp.js — Vitelli Moda PDP behaviour (BUILD CONTRACT v2, §3)

  Drives, and only drives, the data-attributes defined in the contract:
    [data-v-rate]    §3.1  rating row -> smooth scroll to the social-proof section
    [data-v-stock]   §3.3  stock count, re-synced on variant change (when present)
    [data-v-shipby]  §3.4  ship-by date + cutoff countdown (ONE shared 1s interval)
    [data-v-tiers]   §3.5  volume tiers, cart-count driven
    [data-v-tabs]    §3.6  tablist with roving tabindex
    [data-v-ann]     §3.7  announcement rotator
    [data-v-galbar]  §3.8  progress bar + arrows for the EXISTING Sense slider
    [data-v-usp]     §3.29 USP card click animation (CSS-driven, class toggle only)

  Rules this file obeys:
    - No dependencies. ES5 only (no arrow functions, no optional chaining, no ??).
    - Every element lookup is guarded: a missing element is a no-op, never a throw.
    - init() is idempotent — safe to run twice (Shopify re-runs section scripts).
    - Re-inits on `shopify:section:load`; sweeps timers on `shopify:section:unload`.
    - It DRIVES the existing Dawn/Sense <slider-component>; it never replaces it.
*/
(function () {
  'use strict';

  /* keep the media list in its natural order on variant change: the theme's
     setActiveMedia(prepend) reorders the DOM (image + thumb jump to front);
     with this flag it scrolls to the image's real position instead, and our
     bar + thumbnail centring follow via the scroll listener. */
  window.__vitelliKeepMediaOrder = true;

  /* Loaded twice (the section tag and main-product both emit it)? Just re-init. */
  if (window.__vitelliPdp && typeof window.__vitelliPdp.refresh === 'function') {
    window.__vitelliPdp.refresh();
    return;
  }

  var FLASH_MS = 1400;
  /* Built from char codes so the source file stays pure ASCII and the glyphs
     survive any re-encoding on the way to Shopify.
     U+2014 EM DASH (matches the server-rendered "<em dash> <euro>0,00"),
     U+00B7 MIDDLE DOT, U+20AC EURO SIGN (fallback currency symbol only). */
  var EM_DASH = String.fromCharCode(0x2014);
  var MIDDOT = String.fromCharCode(0x00B7);
  var DEFAULT_CURRENCY = String.fromCharCode(0x20AC);

  /* ------------------------------------------------------------------ *
   * helpers
   * ------------------------------------------------------------------ */

  function qsa(root, sel) {
    var out = [];
    if (!root || !root.querySelectorAll) return out;
    var list;
    try { list = root.querySelectorAll(sel); } catch (e) { return out; }
    for (var i = 0; i < list.length; i++) out.push(list[i]);
    return out;
  }

  function qs(root, sel) {
    if (!root || !root.querySelector) return null;
    try { return root.querySelector(sel); } catch (e) { return null; }
  }

  function trim(s) {
    return String(s === null || typeof s === 'undefined' ? '' : s).replace(/^\s+|\s+$/g, '');
  }

  function attr(el, name, fallback) {
    if (!el || !el.getAttribute) return fallback;
    var v = el.getAttribute(name);
    if (v === null || v === '') return fallback;
    return v;
  }

  function intAttr(el, name, fallback) {
    var v = parseInt(attr(el, name, ''), 10);
    return isNaN(v) ? fallback : v;
  }

  function boolAttr(el, name, fallback) {
    if (!el || !el.hasAttribute || !el.hasAttribute(name)) return fallback;
    var v = trim(el.getAttribute(name)).toLowerCase();
    if (v === '') return fallback;
    return !(v === 'false' || v === '0' || v === 'no' || v === 'null' || v === 'nil');
  }

  function text(el) {
    if (!el) return '';
    return trim(String(el.textContent || '').replace(/\s+/g, ' '));
  }

  function setText(el, value) {
    if (!el) return;
    if (el.textContent !== value) el.textContent = value;
  }

  function addClass(el, c) { if (el && el.classList) el.classList.add(c); }
  function removeClass(el, c) { if (el && el.classList) el.classList.remove(c); }
  function setClass(el, c, on) { if (on) addClass(el, c); else removeClass(el, c); }

  function inDoc(el) {
    if (!el) return false;
    if (typeof el.isConnected === 'boolean') return el.isConnected;
    return !!(document.documentElement && document.documentElement.contains(el));
  }

  function closestSel(el, sel) {
    if (!el) return null;
    if (el.closest) { try { return el.closest(sel); } catch (e) { /* fall through */ } }
    var node = el;
    while (node && node.nodeType === 1) {
      var m = node.matches || node.msMatchesSelector || node.webkitMatchesSelector;
      if (m) { try { if (m.call(node, sel)) return node; } catch (e2) { return null; } }
      node = node.parentNode;
    }
    return null;
  }

  function on(target, name, fn, opts) {
    if (!target || !target.addEventListener) return;
    try { target.addEventListener(name, fn, opts); }
    catch (e) { target.addEventListener(name, fn, false); }
  }

  function passiveOpts() {
    var supported = false;
    try {
      var probe = Object.defineProperty({}, 'passive', {
        get: function () { supported = true; return true; }
      });
      window.addEventListener('__vprobe', null, probe);
      window.removeEventListener('__vprobe', null, probe);
    } catch (e) { supported = false; }
    return supported ? { passive: true } : false;
  }
  var PASSIVE = passiveOpts();

  function reducedMotion() {
    try {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) { return false; }
  }

  function pad2(n) {
    n = Math.floor(Math.abs(n));
    return (n < 10 ? '0' : '') + n;
  }

  function raf(fn) {
    if (window.requestAnimationFrame) return window.requestAnimationFrame(fn);
    return window.setTimeout(fn, 16);
  }

  /* Show/hide that survives a `display:` rule from the stylesheet. */
  var REDUCED_MOTION = false;
  try {
    REDUCED_MOTION = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (e) { /* no-op */ }

  function setShown(el, shown) {
    if (!el) return;
    if (shown) {
      if (el.hasAttribute && el.hasAttribute('hidden')) el.removeAttribute('hidden');
      if (el.style) el.style.removeProperty('display');
    } else {
      if (el.setAttribute) el.setAttribute('hidden', '');
      if (el.style) el.style.setProperty('display', 'none');
    }
  }

  /* ------------------------------------------------------------------ *
   * 1. Rating row  [data-v-rate]  (§3.1)
   * ------------------------------------------------------------------ */

  function resolveRateTarget(el) {
    var sel = trim(attr(el, 'data-target', ''));
    var t = null;

    if (sel) {
      try { t = document.querySelector(sel); } catch (e) { t = null; }
      if (!t) {
        var bare = sel.charAt(0) === '#' ? sel.slice(1) : sel;
        if (bare) t = document.getElementById(bare);
      }
    }
    if (!t) t = qs(document, '[id*="social_proof"], [id*="social-proof"]');
    if (t && (t === el || (t.contains && t.contains(el)))) t = null;
    return t;
  }

  function flash(target) {
    if (!target || !target.classList) return;
    if (target.__vFlashTimer) window.clearTimeout(target.__vFlashTimer);
    addClass(target, 'v-flash');
    target.__vFlashTimer = window.setTimeout(function () {
      removeClass(target, 'v-flash');
      target.__vFlashTimer = null;
    }, FLASH_MS);
  }

  function initRating(el) {
    if (el.__vInit) return;
    el.__vInit = true;

    on(el, 'click', function (event) {
      /* preventDefault FIRST: href is "#", so bailing out before this would
         let the browser jump the page to the top. */
      if (event && event.preventDefault) event.preventDefault();
      var target = resolveRateTarget(el);
      if (!target) return;
      if (target.scrollIntoView) {
        try { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
        catch (e) { target.scrollIntoView(true); }
      }
      flash(target);
    });
  }

  /* ------------------------------------------------------------------ *
   * 2. Ship-by + cutoff countdown  [data-v-shipby]  (§3.4)
   *    ONE shared 1s interval drives every instance on the page.
   * ------------------------------------------------------------------ */

  var shipbyItems = [];
  var shipbyTimer = null;

  /* "now" as wall-clock in `tz`, expressed as a local-time Date. */
  function tzNow(tz) {
    var d = new Date();
    if (!tz) return d;
    try {
      var s = d.toLocaleString('en-US', {
        timeZone: tz, hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
      var m = s.match(/(\d+)\D+(\d+)\D+(\d+)\D+(\d+)\D+(\d+)\D+(\d+)/);
      if (!m) return d;
      var out = new Date(
        parseInt(m[3], 10), parseInt(m[1], 10) - 1, parseInt(m[2], 10),
        parseInt(m[4], 10) % 24, parseInt(m[5], 10), parseInt(m[6], 10), 0
      );
      return isNaN(out.getTime()) ? d : out;
    } catch (e) { return d; }
  }

  function formatShipDate(d, locale) {
    var opts = { weekday: 'short', day: 'numeric', month: 'short' };
    var s = '';
    try { s = d.toLocaleDateString(locale ? locale : undefined, opts); } catch (e) { s = ''; }
    if (!s) { try { s = d.toLocaleDateString(undefined, opts); } catch (e2) { s = ''; } }
    if (!s) s = d.toDateString();
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  function updateShipby(el) {
    var st = el.__vShip;
    if (!st) return;

    var cutoffHour = intAttr(el, 'data-cutoff', 22);
    if (cutoffHour < 0) cutoffHour = 0;
    if (cutoffHour > 23) cutoffHour = 23;

    var lead = intAttr(el, 'data-lead', 1);
    if (lead < 0) lead = 0;
    if (lead > 60) lead = 60;

    var weekend = boolAttr(el, 'data-weekend', true);
    var locale = trim(attr(el, 'data-locale', ''));
    var now = tzNow(trim(attr(el, 'data-tz', '')));

    /* The cutoff we are counting down to. */
    var cut = new Date(now.getFullYear(), now.getMonth(), now.getDate(), cutoffHour, 0, 0, 0);
    var beforeCutoff = now.getTime() < cut.getTime();
    if (!beforeCutoff) {
      cut = new Date(cut.getFullYear(), cut.getMonth(), cut.getDate() + 1, cutoffHour, 0, 0, 0);
    }

    var secs = Math.max(0, Math.floor((cut.getTime() - now.getTime()) / 1000));
    var clock = pad2(Math.floor(secs / 3600)) + ':' + pad2(Math.floor((secs % 3600) / 60)) + ':' + pad2(secs % 60);

    /* Ship date = cutoff day + lead days, anchored at midday so a DST shift
       can never move it across a day boundary. */
    var ship = new Date(cut.getFullYear(), cut.getMonth(), cut.getDate() + lead, 12, 0, 0, 0);
    if (weekend) {
      var guard = 0;
      while ((ship.getDay() === 0 || ship.getDay() === 6) && guard < 7) {
        ship.setDate(ship.getDate() + 1);
        guard++;
      }
    }

    var dateText = formatShipDate(ship, locale);
    var label = beforeCutoff
      ? attr(el, 'data-suffix-today', '')
      : attr(el, 'data-suffix-next', '');

    if (st.dateEl && dateText !== st.lastDate) { setText(st.dateEl, dateText); st.lastDate = dateText; }
    if (st.clockEl && clock !== st.lastClock) { setText(st.clockEl, clock); st.lastClock = clock; }
    if (st.labelEl && label !== st.lastLabel) { setText(st.labelEl, label); st.lastLabel = label; }
  }

  function shipbyTick() {
    for (var i = shipbyItems.length - 1; i >= 0; i--) {
      var el = shipbyItems[i];
      if (!inDoc(el)) { shipbyItems.splice(i, 1); continue; }
      try { updateShipby(el); } catch (e) { /* never let one instance kill the interval */ }
    }
    if (!shipbyItems.length && shipbyTimer) {
      window.clearInterval(shipbyTimer);
      shipbyTimer = null;
    }
  }

  function initShipby(el) {
    if (el.__vInit) return;
    el.__vInit = true;

    el.__vShip = {
      dateEl: qs(el, '[data-v-shipdate]'),
      clockEl: qs(el, '[data-v-clock]'),
      labelEl: qs(el, '[data-v-cutlabel]'),
      lastDate: null, lastClock: null, lastLabel: null
    };
    shipbyItems.push(el);
    try { updateShipby(el); } catch (e) { /* no-op */ }
    if (!shipbyTimer) shipbyTimer = window.setInterval(shipbyTick, 1000);
  }

  /* ------------------------------------------------------------------ *
   * 3. Volume tiers  [data-v-tiers]  (§3.5)
   * ------------------------------------------------------------------ */

  var tierItems = [];
  var cartRefreshTimer = null;

  function cartUrl() {
    var root = '/';
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      root = window.Shopify.routes.root;
    }
    return root.replace(/\/+$/, '') + '/cart.js';
  }

  function parseTiers(el) {
    var out = [];
    var raw = trim(attr(el, 'data-tiers', ''));
    if (raw) {
      var parsed = null;
      try { parsed = JSON.parse(raw); } catch (e) { parsed = null; }
      if (parsed && parsed.length) {
        for (var i = 0; i < parsed.length; i++) {
          var row = parsed[i];
          if (!row) continue;
          var q = parseInt(row.length ? row[0] : row.qty, 10);
          var p = parseFloat(row.length ? row[1] : row.pct);
          if (isNaN(q) || q < 1) continue;
          out.push({ qty: q, pct: isNaN(p) ? 0 : p });
        }
      }
    }
    /* Fallback: rebuild from the rendered buttons so a malformed JSON string
       still leaves the block usable. */
    if (!out.length) {
      var btns = qsa(el, '[data-v-tier]');
      for (var j = 0; j < btns.length; j++) {
        var bq = intAttr(btns[j], 'data-qty', 0);
        if (bq < 1) continue;
        var bp = parseFloat(text(qs(btns[j], '.v-tier__pct')).replace(/[^\d.,-]/g, '').replace(',', '.'));
        out.push({ qty: bq, pct: isNaN(bp) ? 0 : bp });
      }
    }
    out.sort(function (a, b) { return a.qty - b.qty; });
    return out;
  }

  function money(amount, symbol, locale) {
    var s = (Math.round(Math.abs(amount) * 100) / 100).toFixed(2);
    if (/^it/i.test(locale || '')) s = s.replace('.', ',');
    return symbol + s;
  }

  function tierButton(st, qty) {
    for (var i = 0; i < st.buttons.length; i++) {
      if (intAttr(st.buttons[i], 'data-qty', -1) === qty) return st.buttons[i];
    }
    return null;
  }

  /* Lead copy is assembled ONLY from strings the merchant already typed into
     the tier buttons (qty / pct / off labels) — no invented display copy. */
  function tierLabelFromButton(btn) {
    if (!btn) return '';
    var parts = [];
    var q = text(qs(btn, '.v-tier__qty'));
    var p = text(qs(btn, '.v-tier__pct'));
    var o = text(qs(btn, '.v-tier__off'));
    if (q) parts.push(q);
    var right = [];
    if (p) right.push(p);
    if (o) right.push(o);
    if (right.length) parts.push(right.join(' '));
    return parts.join(' ' + MIDDOT + ' ');
  }

  function renderTiers(st) {
    var tiers = st.tiers;
    var count = st.count;

    var activeIdx = -1;
    for (var i = 0; i < tiers.length; i++) {
      if (count >= tiers[i].qty) activeIdx = i;
    }
    var activeQty = activeIdx >= 0 ? tiers[activeIdx].qty : -1;
    var activePct = activeIdx >= 0 ? tiers[activeIdx].pct : 0;

    for (var b = 0; b < st.buttons.length; b++) {
      setClass(st.buttons[b], 'is-active', intAttr(st.buttons[b], 'data-qty', -1) === activeQty);
    }

    /* The tier discounts are CART-LEVEL automatic discounts with a minimum
       quantity, so the percentage applies to the whole cart's value — not to
       this one product. Use the real cart total as soon as /cart.js has
       resolved; until then keep the first-paint estimate built from the
       rendered data-count. */
    var savings = 0;
    if (activeIdx >= 0) {
      savings = (st.cartTotal !== null ? st.cartTotal : st.price * count) * activePct / 100;
    }

    if (st.save) {
      setText(st.save, EM_DASH + ' ' + money(savings, st.currency, st.locale));
      setClass(st.save, 'v-tiers__save--zero', !(savings > 0));
    }

    if (st.fill) {
      var maxQty = tiers.length ? tiers[tiers.length - 1].qty : 0;
      var pct = maxQty > 0 ? Math.min(100, (count / maxQty) * 100) : 0;
      var w = (Math.round(pct * 10) / 10) + '%';
      if (st.fill.style && st.fill.style.width !== w) st.fill.style.width = w;
    }

    if (st.lead) {
      var leadText;
      if (count <= 0) {
        leadText = st.empty;
      } else {
        var refIdx = activeIdx >= 0 ? activeIdx : 0;
        var ref = tiers.length ? tiers[refIdx] : null;
        leadText = tierLabelFromButton(ref ? tierButton(st, ref.qty) : null);
      }
      if (leadText) setText(st.lead, leadText);
      else if (count <= 0 && st.empty) setText(st.lead, st.empty);
    }
  }

  /* The one place a fetched cart lands: the unit count drives the tier ladder,
     the total (cents, optional) drives the savings figure. */
  function setCartData(n, totalCents) {
    var count = parseInt(n, 10);
    if (isNaN(count) || count < 0) return;
    var cents = parseInt(totalCents, 10);
    var total = (isNaN(cents) || cents < 0) ? null : cents / 100;
    for (var i = tierItems.length - 1; i >= 0; i--) {
      var el = tierItems[i];
      if (!inDoc(el)) { tierItems.splice(i, 1); continue; }
      if (!el.__vTiers) continue;
      el.__vTiers.count = count;
      if (total !== null) el.__vTiers.cartTotal = total;
      try { renderTiers(el.__vTiers); } catch (e) { /* no-op */ }
    }
  }

  function refreshCart() {
    if (typeof window.fetch !== 'function') return;
    if (!tierItems.length) return;
    try {
      window.fetch(cartUrl(), {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      }).then(function (res) {
        return res && res.ok ? res.json() : null;
      }).then(function (data) {
        if (data && typeof data.item_count !== 'undefined') setCartData(data.item_count, data.total_price);
      })['catch'](function () { /* offline / blocked -> keep the rendered count */ });
    } catch (e) { /* no-op */ }
  }

  function scheduleCartRefresh(delay) {
    if (cartRefreshTimer) window.clearTimeout(cartRefreshTimer);
    cartRefreshTimer = window.setTimeout(function () {
      cartRefreshTimer = null;
      refreshCart();
    }, typeof delay === 'number' ? delay : 200);
  }

  function initTiers(el) {
    if (el.__vInit) return;
    el.__vInit = true;

    var priceCents = intAttr(el, 'data-price', 0);
    var st = {
      el: el,
      tiers: parseTiers(el),
      price: (priceCents > 0 ? priceCents : 0) / 100,
      currency: attr(el, 'data-currency', DEFAULT_CURRENCY),
      locale: trim(attr(el, 'data-locale', '')) || trim(document.documentElement.lang) || 'it',
      empty: attr(el, 'data-empty', ''),
      count: Math.max(0, intAttr(el, 'data-count', 0)),
      /* Real cart value in currency units — null until /cart.js resolves. */
      cartTotal: null,
      buttons: qsa(el, '[data-v-tier]'),
      lead: qs(el, '[data-v-cartlead]'),
      save: qs(el, '[data-v-cartsave]'),
      fill: qs(el, '[data-v-meterfill]')
    };
    el.__vTiers = st;
    tierItems.push(el);

    /* The tier cards are DISPLAY-ONLY: they show which cart-level discount the
       cart has reached. There is no quantity input on this template and the
       discounts are automatic, so there is nothing to click. */
    try { renderTiers(st); } catch (e) { /* no-op */ }
  }

  /* ------------------------------------------------------------------ *
   * 4. Tabs  [data-v-tabs]  (§3.6)
   * ------------------------------------------------------------------ */

  function tabPanelFor(root, btn, index, panels) {
    var id = trim(attr(btn, 'aria-controls', ''));
    if (id) {
      var byId = document.getElementById(id);
      if (byId) return byId;
    }
    return panels[index] || null;
  }

  function selectTab(st, index, focusBtn) {
    if (!st.buttons.length) return;
    if (index < 0) index = st.buttons.length - 1;
    if (index >= st.buttons.length) index = 0;

    for (var i = 0; i < st.buttons.length; i++) {
      var btn = st.buttons[i];
      var panel = st.panels[i];
      var on_ = (i === index);
      if (btn) {
        btn.setAttribute('aria-selected', on_ ? 'true' : 'false');
        btn.setAttribute('tabindex', on_ ? '0' : '-1');
        setClass(btn, 'is-active', on_);
      }
      if (panel) {
        if (on_) panel.removeAttribute('hidden');
        else panel.setAttribute('hidden', '');
      }
    }
    st.index = index;
    if (focusBtn && st.buttons[index] && st.buttons[index].focus) {
      try { st.buttons[index].focus(); } catch (e) { /* no-op */ }
    }
  }

  function initTabs(el) {
    if (el.__vInit) return;
    el.__vInit = true;

    var buttons = qsa(el, '[data-v-tabbtn]');
    if (!buttons.length) return;
    var rawPanels = qsa(el, '[data-v-tabpanel]');

    var panels = [];
    for (var i = 0; i < buttons.length; i++) panels.push(tabPanelFor(el, buttons[i], i, rawPanels));

    var st = { el: el, buttons: buttons, panels: panels, index: 0 };
    el.__vTabs = st;

    /* Honour whatever Liquid rendered as selected; fall back to the first tab. */
    var start = 0;
    for (var s = 0; s < buttons.length; s++) {
      if (trim(attr(buttons[s], 'aria-selected', '')) === 'true') { start = s; break; }
    }

    for (var b = 0; b < buttons.length; b++) {
      (function (btn, idx) {
        on(btn, 'click', function (event) {
          if (event && event.preventDefault) event.preventDefault();
          selectTab(st, idx, false);
        });
        on(btn, 'keydown', function (event) {
          var key = event.key || event.keyIdentifier || '';
          var next = null;
          if (key === 'ArrowRight' || key === 'Right') next = idx + 1;
          else if (key === 'ArrowLeft' || key === 'Left') next = idx - 1;
          else if (key === 'Home') next = 0;
          else if (key === 'End') next = st.buttons.length - 1;
          if (next === null) return;
          if (event.preventDefault) event.preventDefault();
          selectTab(st, next, true);
        });
      }(buttons[b], b));
    }

    selectTab(st, start, false);
  }

  /* ------------------------------------------------------------------ *
   * 5. Announcement rotator  [data-v-ann]  (§3.7)
   * ------------------------------------------------------------------ */

  var annItems = [];

  function annGo(st, index) {
    var n = st.msgs.length;
    if (!n) return;
    index = ((index % n) + n) % n;
    for (var i = 0; i < n; i++) setClass(st.msgs[i], 'is-on', i === index);
    st.index = index;
  }

  function annStop(st) {
    if (st.timer) { window.clearInterval(st.timer); st.timer = null; }
  }

  function annStart(st) {
    if (st.timer) return;
    if (!st.auto || st.msgs.length < 2) return;
    if (st.hover || st.paused) return;
    if (document.hidden) return;
    if (reducedMotion()) return;
    st.timer = window.setInterval(function () {
      if (!inDoc(st.el)) { annStop(st); return; }
      annGo(st, st.index + 1);
    }, st.speed);
  }

  function annRestart(st) {
    annStop(st);
    annStart(st);
  }

  function initAnnouncement(el) {
    if (el.__vInit) return;
    el.__vInit = true;

    var msgs = qsa(el, '[data-v-annmsg]');
    var prev = qs(el, '[data-v-annprev]');
    var next = qs(el, '[data-v-annnext]');

    var speed = intAttr(el, 'data-speed', 5000);
    if (isNaN(speed) || speed < 1000) speed = 5000;

    var st = {
      el: el,
      msgs: msgs,
      index: 0,
      timer: null,
      hover: false,
      paused: false,
      auto: boolAttr(el, 'data-auto', true),
      speed: speed
    };
    el.__vAnn = st;
    annItems.push(el);

    if (!msgs.length) {
      setShown(prev, false);
      setShown(next, false);
      return;
    }

    /* One message: nothing to rotate between, so the arrows go away. */
    if (msgs.length < 2) {
      setShown(prev, false);
      setShown(next, false);
      annGo(st, 0);
      return;
    }

    var start = 0;
    for (var i = 0; i < msgs.length; i++) {
      if (msgs[i].classList && msgs[i].classList.contains('is-on')) { start = i; break; }
    }
    annGo(st, start);

    if (prev) {
      on(prev, 'click', function (event) {
        if (event && event.preventDefault) event.preventDefault();
        annGo(st, st.index - 1);
        annRestart(st);
      });
    }
    if (next) {
      on(next, 'click', function (event) {
        if (event && event.preventDefault) event.preventDefault();
        annGo(st, st.index + 1);
        annRestart(st);
      });
    }

    on(el, 'mouseenter', function () { st.hover = true; annStop(st); });
    on(el, 'mouseleave', function () { st.hover = false; annStart(st); });

    annStart(st);
  }

  function annVisibilitySweep() {
    for (var i = annItems.length - 1; i >= 0; i--) {
      var el = annItems[i];
      if (!inDoc(el)) {
        if (el.__vAnn) annStop(el.__vAnn);
        annItems.splice(i, 1);
        continue;
      }
      if (!el.__vAnn) continue;
      if (document.hidden) annStop(el.__vAnn);
      else annStart(el.__vAnn);
    }
  }

  /* ------------------------------------------------------------------ *
   * 6. Gallery progress bar + arrows  [data-v-galbar]  (§3.8)
   *    Drives the EXISTING <slider-component> — never a second slider.
   * ------------------------------------------------------------------ */

  function findGalleryScroller(gal) {
    if (!gal) return null;
    /* The main viewer only. `[id^="Slider-"]` also matches the thumbnail strip,
       so scope to GalleryViewer first (recon/gallery.md §2). */
    var viewer = qs(gal, '[id^="GalleryViewer"]');
    var scope = viewer || gal;
    return qs(scope, '[id^="Slider-Gallery"]')
      || qs(scope, 'ul[id^="Slider-"]')
      || qs(scope, '.product__media-list');
  }

  /* assets/vitelli-pdp.css drives .v-galbar__fill from --v-gal-w / --v-gal-x
     (`width: var(--v-gal-w, 100%)`, `transform: translateX(var(--v-gal-x, 0%))`)
     so the stylesheet keeps ownership of the transition. Pass null to leave a
     value untouched. Falls back to plain inline styles on engines without
     CSS custom properties. */
  function galSetFill(fill, w, x) {
    if (!fill || !fill.style) return;
    var supportsVars = typeof fill.style.setProperty === 'function';
    if (supportsVars) {
      if (w !== null) fill.style.setProperty('--v-gal-w', w);
      if (x !== null) fill.style.setProperty('--v-gal-x', x);
    } else {
      if (w !== null) fill.style.width = w;
      if (x !== null) fill.style.transform = 'translateX(' + x + ')';
    }
  }

  function galSlides(scroller) {
    var all = qsa(scroller, '[id^="Slide-"]');
    if (!all.length) all = qsa(scroller, '.slider__slide');
    var visible = [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].clientWidth > 0) visible.push(all[i]);
    }
    return visible;
  }

  function galStep(st) {
    if (st.slides.length > 1) {
      var d = st.slides[1].offsetLeft - st.slides[0].offsetLeft;
      if (d > 1) return d;
    }
    return st.scroller.clientWidth || 1;
  }

  /* optimistic paint: on an arrow/thumbnail CLICK the target index is already
     known, so the bar and thumbnail centring respond instantly instead of
     waiting for the smooth scroll to pass its halfway point. The scroll
     painter is suppressed until it reaches the same index (1.2s fail-safe). */
  function galGoto(st, idx) {
    if (!st.active || st.total < 2) return;
    if (idx < 0) idx = 0;
    if (idx > st.total - 1) idx = st.total - 1;
    st.forced = idx;
    st.forcedAt = Date.now();
    st.index = idx;
    st.painted = true;
    galSetFill(st.fill, null, (idx * 100) + '%');
    setText(st.count, (idx + 1) + ' / ' + st.total);
    galCenterThumb(st);
  }

  /* animate a scroller to a target scrollLeft over the SAME 280ms ease the
     progress bar uses, so both move in lockstep. Finishing well inside the
     theme's 500ms debounced thumbnail-sync also means media-gallery.js finds
     the active thumb already visible and never issues its instant jump. */
  function galAnimScroll(st, list, target) {
    if (st.thumbRaf) { cancelAnimationFrame(st.thumbRaf); st.thumbRaf = null; }
    var from = list.scrollLeft;
    var delta = target - from;
    if (delta > -1 && delta < 1) return;
    if (REDUCED_MOTION) { list.scrollLeft = target; return; }
    var t0 = (window.performance && performance.now) ? performance.now() : Date.now();
    var DUR = 280;
    function frame() {
      var now = (window.performance && performance.now) ? performance.now() : Date.now();
      var t = (now - t0) / DUR;
      if (t >= 1) { list.scrollLeft = target; st.thumbRaf = null; return; }
      var e = 1 - Math.pow(1 - t, 3); /* easeOutCubic ~ CSS ease */
      list.scrollLeft = from + delta * e;
      st.thumbRaf = requestAnimationFrame(frame);
    }
    st.thumbRaf = requestAnimationFrame(frame);
  }

  /* keep the active thumbnail centred in its row; the clamp means it parks
     at the row's start/end once the carousel reaches either extreme. */
  function galMarkThumb(list, item) {
    var old = qsa(list, '[aria-current]');
    for (var i = 0; i < old.length; i++) old[i].removeAttribute('aria-current');
    var btn = qs(item, 'button') || item;
    btn.setAttribute('aria-current', true);
  }

  function galCenterThumb(st) {
    var list = st.thumbs;
    if (!list || !list.clientWidth) return;
    var items = qsa(list, '.thumbnail-list__item');
    var t = items[st.index];
    if (!t) return;
    galMarkThumb(list, t);
    var target = t.offsetLeft - (list.clientWidth - t.offsetWidth) / 2;
    var max = list.scrollWidth - list.clientWidth;
    if (target < 0) target = 0;
    if (target > max) target = max;
    galAnimScroll(st, list, target);
  }

  function galMeasure(st) {
    st.slides = galSlides(st.scroller);
    st.total = st.slides.length;
    var scrollable = st.scroller.scrollWidth - st.scroller.clientWidth > 2;
    st.active = (st.total >= 2 && scrollable);

    setShown(st.bar, st.active);
    setShown(st.prev, st.active);
    setShown(st.next, st.active);

    if (!st.active) return;
    galSetFill(st.fill, (100 / st.total) + '%', null);
    st.painted = false;
    galPaint(st);
  }

  function galPaint(st) {
    if (!st.active || st.total < 2) return;
    var max = st.scroller.scrollWidth - st.scroller.clientWidth;
    var idx = max > 0
      ? Math.round((st.scroller.scrollLeft / max) * (st.total - 1))
      : 0;
    if (idx < 0) idx = 0;
    if (idx > st.total - 1) idx = st.total - 1;
    if (st.forced != null) {
      if (idx === st.forced || Date.now() - st.forcedAt > 1200) st.forced = null;
      else return;
    }
    if (idx === st.index && st.painted) return;
    st.index = idx;
    st.painted = true;
    galSetFill(st.fill, null, (idx * 100) + '%');
    setText(st.count, (idx + 1) + ' / ' + st.total);
    galCenterThumb(st);
  }

  function initGalbar(el) {
    if (el.__vInit) return;
    el.__vInit = true;

    var gal = closestSel(el, 'media-gallery') || qs(document, 'media-gallery');
    var scroller = findGalleryScroller(gal);
    if (!gal || !scroller) { setShown(el, false); return; }

    var st = {
      el: el,
      gal: gal,
      scroller: scroller,
      fill: qs(el, '[data-v-galfill]'),
      count: qs(el, '[data-v-galcount]'),
      bar: el,
      prev: qs(gal, '[data-v-galprev]'),
      next: qs(gal, '[data-v-galnext]'),
      thumbs: qs(gal, '.thumbnail-list'),
      slides: [],
      total: 0,
      index: -1,
      painted: false,
      active: false,
      ticking: false
    };
    el.__vGal = st;

    if (st.thumbs) {
      on(st.thumbs, 'click', function (ev) {
        var t = ev.target;
        var item = null;
        while (t && t !== st.thumbs) {
          if (t.className && String(t.className).indexOf('thumbnail-list__item') !== -1) { item = t; break; }
          t = t.parentNode;
        }
        if (!item) return;
        var items = qsa(st.thumbs, '.thumbnail-list__item');
        for (var k = 0; k < items.length; k++) {
          if (items[k] === item) { galGoto(st, k); break; }
        }
      });
    }

    on(scroller, 'scroll', function () {
      if (st.ticking) return;
      st.ticking = true;
      raf(function () {
        st.ticking = false;
        try { galPaint(st); } catch (e) { /* no-op */ }
      });
    }, PASSIVE);

    function nudge(dir) {
      var by = st.scroller.clientWidth || 0;
      if (!by) return;
      galGoto(st, st.index + dir);
      if (st.scroller.scrollBy) {
        try { st.scroller.scrollBy({ left: dir * by, behavior: 'smooth' }); return; }
        catch (e) { /* fall through */ }
      }
      st.scroller.scrollLeft = st.scroller.scrollLeft + dir * by;
    }

    if (st.prev) {
      on(st.prev, 'click', function (event) {
        if (event && event.preventDefault) event.preventDefault();
        nudge(-1);
      });
    }
    if (st.next) {
      on(st.next, 'click', function (event) {
        if (event && event.preventDefault) event.preventDefault();
        nudge(1);
      });
    }

    galMeasure(st);
    /* Images arriving late change scrollWidth — re-measure once loaded. */
    on(window, 'load', function () { if (inDoc(el)) galMeasure(st); });
  }

  var galResizeTimer = null;
  function galResizeSweep() {
    if (galResizeTimer) window.clearTimeout(galResizeTimer);
    galResizeTimer = window.setTimeout(function () {
      galResizeTimer = null;
      var bars = qsa(document, '[data-v-galbar]');
      for (var i = 0; i < bars.length; i++) {
        var st = bars[i].__vGal;
        if (!st || !inDoc(bars[i])) continue;
        st.painted = false;
        try { galMeasure(st); } catch (e) { /* no-op */ }
      }
    }, 150);
  }

  /* ------------------------------------------------------------------ *
   * 7. Theme pub/sub bus — variant changes + cart updates
   *
   *    <product-info> re-renders only the price / Inventory / Sku / Volume /
   *    Price-Per-Item ids, so our blocks would freeze on the first variant.
   *    The theme announces both events on the Dawn bus instead:
   *      assets/global.js  -> publish(PUB_SUB_EVENTS.variantChange,
   *                             { data: { sectionId, html, variant } })
   *      assets/product-form.js -> publish(PUB_SUB_EVENTS.cartUpdate, {…})
   *    Everything below is a silent no-op when that bus is not on the page.
   * ------------------------------------------------------------------ */

  var busVariantBound = false;
  var busCartBound = false;

  /* assets/constants.js declares PUB_SUB_EVENTS with `const`, which makes it a
     global LEXICAL binding and NOT a property of window — so check window first
     (in case a theme update exports it there) and then the bare global. The
     literals are that file's own values, used only if neither resolves. */
  function busEvent(key, verified) {
    var events = null;
    if (window.PUB_SUB_EVENTS) events = window.PUB_SUB_EVENTS;
    if (!events) {
      try {
        if (typeof PUB_SUB_EVENTS !== 'undefined') events = PUB_SUB_EVENTS;
      } catch (e) { events = null; }
    }
    if (events && events[key]) return events[key];
    return verified;
  }

  /* `function subscribe(){}` in assets/pubsub.js IS a window property. */
  function busSubscriber() {
    if (typeof window.subscribe === 'function') return window.subscribe;
    try { if (typeof subscribe === 'function') return subscribe; } catch (e) { /* no-op */ }
    return null;
  }

  function busPayload(payload) {
    if (!payload) return null;
    return payload.data ? payload.data : payload;
  }

  /* The variant-change payload also carries `html`: the section as Shopify just
     re-rendered it for the new variant. Its [data-v-stock] count is Liquid's own
     output — cap, fallback and show_live_inventory already applied — so copy it
     verbatim rather than recomputing it here. */
  function stockTextFromHtml(node) {
    if (!node) return null;
    var b = qs(node, 'b');
    if (!b) return null;
    var t = String(b.textContent || '');
    return /\d/.test(t) ? t : null;
  }

  /* Fallback when the payload has no html: recompute from the variant, but ONLY
     if the card publishes the rules it was rendered with. Without data-cap /
     data-fallback we cannot know the merchant's cap, so the server-rendered
     number is left exactly as it is. */
  function stockTextFromVariant(card, b, variant) {
    var cap = intAttr(card, 'data-cap', -1);
    var fallback = intAttr(card, 'data-fallback', -1);
    if (cap < 1) cap = intAttr(b, 'data-cap', -1);
    if (fallback < 1) fallback = intAttr(b, 'data-fallback', -1);
    if (cap < 1 || fallback < 1) return null;

    var n = fallback;
    if (variant && variant.inventory_management === 'shopify' &&
        typeof variant.inventory_quantity === 'number' && variant.inventory_quantity > 0) {
      n = Math.min(variant.inventory_quantity, cap);
    }
    var current = String(b.textContent || '');
    if (!/\d/.test(current)) return null;
    return current.replace(/\d+/, String(n));
  }

  function updateStock(variant, doc) {
    var cards = qsa(document, '[data-v-stock]');
    if (!cards.length) return;
    var fresh = (doc && doc.querySelectorAll) ? qsa(doc, '[data-v-stock]') : [];
    for (var i = 0; i < cards.length; i++) {
      var b = qs(cards[i], 'b');
      if (!b) continue;
      var next = stockTextFromHtml(fresh[i]);
      if (next === null) next = stockTextFromVariant(cards[i], b, variant);
      if (next !== null) setText(b, next);
    }
  }

  /* variant.price is in cents, same convention as the rendered data-price. */
  function updateTierPrice(variant) {
    var cents = (variant && typeof variant.price !== 'undefined') ? parseInt(variant.price, 10) : NaN;
    var hasPrice = !isNaN(cents) && cents >= 0;
    for (var i = tierItems.length - 1; i >= 0; i--) {
      var el = tierItems[i];
      if (!inDoc(el)) { tierItems.splice(i, 1); continue; }
      if (hasPrice && el.setAttribute) el.setAttribute('data-price', String(cents));
      if (!el.__vTiers) continue;
      if (hasPrice) el.__vTiers.price = cents / 100;
      /* Re-render either way so the active tier is repainted on every change. */
      try { renderTiers(el.__vTiers); } catch (e) { /* no-op */ }
    }
  }

  function onVariantChange(payload) {
    var d = busPayload(payload);
    var variant = d ? d.variant : null;
    var doc = d ? d.html : null;
    try { updateStock(variant, doc); } catch (e) { /* no-op */ }
    try { updateTierPrice(variant); } catch (e) { /* no-op */ }
  }

  function bindThemeBus() {
    if (busVariantBound && busCartBound) return;
    var sub = busSubscriber();
    if (!sub) return; /* no bus on the page -> silent no-op */
    if (!busVariantBound) {
      try {
        sub(busEvent('variantChange', 'variant-change'), onVariantChange);
        busVariantBound = true;
      } catch (e) { /* no-op */ }
    }
    if (!busCartBound) {
      try {
        sub(busEvent('cartUpdate', 'cart-update'), function () { scheduleCartRefresh(150); });
        busCartBound = true;
      } catch (e) { /* no-op */ }
    }
  }

  /* ------------------------------------------------------------------ *
   * init / re-init
   * ------------------------------------------------------------------ */

  function each(sel, fn) {
    var list = qsa(document, sel);
    for (var i = 0; i < list.length; i++) {
      try { fn(list[i]); } catch (e) { /* one bad instance must not stop the rest */ }
    }
  }

  /* ------------------------------------------------------------------ *
   * 8. Variant option rows -> one-line scroller (.v-optrow)
   *    A <fieldset> cannot be the scroll container itself: its <legend>
   *    straddles the top border, so overflow clips the label's top half
   *    ("Colore"/"Taglia" half readable). Moving the options into an inner
   *    wrapper scrolls the row while the legend stays a normal label.
   *    Radio change events still bubble to <variant-radios> unchanged.
   * ------------------------------------------------------------------ */
  function initOptrow(fs) {
    if (fs.__vOptrow) return;
    fs.__vOptrow = true;
    if (qs(fs, '.v-optrow')) return;
    var row = document.createElement('div');
    row.className = 'v-optrow';
    var kids = [];
    var i;
    for (i = 0; i < fs.childNodes.length; i++) {
      var n = fs.childNodes[i];
      if (n.tagName && String(n.tagName).toLowerCase() === 'legend') continue;
      kids.push(n);
    }
    for (i = 0; i < kids.length; i++) row.appendChild(kids[i]);

    var wrap = document.createElement('div');
    wrap.className = 'v-optwrap';
    wrap.appendChild(row);
    var aPrev = optArrow('prev', 'Opzioni precedenti');
    var aNext = optArrow('next', 'Altre opzioni');
    wrap.appendChild(aPrev);
    wrap.appendChild(aNext);
    fs.appendChild(wrap);

    function paint() {
      var max = row.scrollWidth - row.clientWidth;
      var sc = max > 2;
      setShown(aPrev, sc && row.scrollLeft > 2);
      setShown(aNext, sc && row.scrollLeft < max - 2);
    }
    on(row, 'scroll', paint, PASSIVE);
    on(window, 'resize', paint);
    on(aPrev, 'click', function () { optNudge(row, -1); });
    on(aNext, 'click', function () { optNudge(row, 1); });
    paint();
  }

  function optArrow(dir, label) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'v-optarw v-optarw--' + dir;
    b.setAttribute('aria-label', label);
    b.setAttribute('hidden', '');
    b.innerHTML = dir === 'prev'
      ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M15 4 7 12l8 8"></path></svg>'
      : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M9 4l8 8-8 8"></path></svg>';
    return b;
  }

  function optNudge(row, dir) {
    var by = Math.max(120, Math.round(row.clientWidth * 0.6));
    if (row.scrollBy) {
      try { row.scrollBy({ left: dir * by, behavior: 'smooth' }); return; } catch (e) { /* no-op */ }
    }
    row.scrollLeft += dir * by;
  }

  function initOptrows() {
    each('.product__info-wrapper fieldset.product-form__input', initOptrow);
    each('.product__info-container fieldset.product-form__input', initOptrow);
  }

  function initAll() {
    each('[data-v-rate]', initRating);
    each('[data-v-shipby]', initShipby);
    each('[data-v-tiers]', initTiers);
    each('[data-v-tabs]', initTabs);
    each('[data-v-ann]', initAnnouncement);
    each('[data-v-galbar]', initGalbar);
    initOptrows();
    /* pubsub.js / constants.js may still have been loading on the first pass. */
    try { bindThemeBus(); } catch (e) { /* no-op */ }
  }

  function sweep() {
    var i;
    for (i = shipbyItems.length - 1; i >= 0; i--) {
      if (!inDoc(shipbyItems[i])) shipbyItems.splice(i, 1);
    }
    for (i = tierItems.length - 1; i >= 0; i--) {
      if (!inDoc(tierItems[i])) tierItems.splice(i, 1);
    }
    for (i = annItems.length - 1; i >= 0; i--) {
      if (!inDoc(annItems[i])) {
        if (annItems[i].__vAnn) annStop(annItems[i].__vAnn);
        annItems.splice(i, 1);
      }
    }
    if (!shipbyItems.length && shipbyTimer) {
      window.clearInterval(shipbyTimer);
      shipbyTimer = null;
    }
  }

  /* --- global listeners, attached exactly once --- */

  on(document, 'visibilitychange', annVisibilitySweep);
  on(window, 'resize', galResizeSweep);
  on(window, 'orientationchange', galResizeSweep);

  /* Cart-count refresh for the tier meter. Attached defensively: these events
     may never fire, and that is fine — the rendered data-count still applies. */
  on(document, 'submit', function (event) {
    var form = event ? event.target : null;
    if (!form || !form.getAttribute) return;
    if (String(form.getAttribute('action') || '').indexOf('/cart/add') === -1) return;
    window.setTimeout(refreshCart, 600);
    window.setTimeout(refreshCart, 1600);
  }, true);

  /* Legacy DOM events: this theme does not emit them, but they are harmless and
     cost nothing if a third-party script does. The real paths are the pub/sub
     bus below and the form-submit hook above. */
  (function bindCartEvents() {
    var names = ['cart:refresh', 'cart-drawer:open'];
    var handler = function () { scheduleCartRefresh(150); };
    for (var i = 0; i < names.length; i++) {
      on(document, names[i], handler);
      on(window, names[i], handler);
    }
  }());

  bindThemeBus();

  on(document, 'shopify:section:load', function () { initAll(); });
  on(document, 'shopify:section:unload', function () { sweep(); });

  /* Theme-editor niceties: selecting a block should reveal it. */
  on(document, 'shopify:block:select', function (event) {
    var el = event ? event.target : null;
    if (!el) return;

    var tabPanel = closestSel(el, '[data-v-tabpanel]');
    var tabBtn = closestSel(el, '[data-v-tabbtn]');
    var tabsRoot = closestSel(el, '[data-v-tabs]');
    if (tabsRoot && tabsRoot.__vTabs) {
      var stT = tabsRoot.__vTabs;
      var idx = -1;
      var k;
      if (tabBtn) { for (k = 0; k < stT.buttons.length; k++) if (stT.buttons[k] === tabBtn) idx = k; }
      if (idx < 0 && tabPanel) { for (k = 0; k < stT.panels.length; k++) if (stT.panels[k] === tabPanel) idx = k; }
      if (idx >= 0) selectTab(stT, idx, false);
    }

    var msg = closestSel(el, '[data-v-annmsg]');
    var annRoot = closestSel(el, '[data-v-ann]');
    if (msg && annRoot && annRoot.__vAnn) {
      var stA = annRoot.__vAnn;
      for (var m = 0; m < stA.msgs.length; m++) {
        if (stA.msgs[m] === msg) { annGo(stA, m); break; }
      }
      stA.paused = true;
      annStop(stA);
    }
  });

  on(document, 'shopify:block:deselect', function (event) {
    var el = event ? event.target : null;
    var annRoot = el ? closestSel(el, '[data-v-ann]') : null;
    if (annRoot && annRoot.__vAnn) {
      annRoot.__vAnn.paused = false;
      annStart(annRoot.__vAnn);
    }
  });

  /* --- USP cards: play the click animation (§3.29) ---------------------- *
   * Delegated on purpose: USP blocks can be added, removed or reordered in
   * the theme editor at any time, and a delegated listener needs no re-init.
   * The class is the ONLY thing JS owns here; every frame lives in the CSS. */
  var USP_PLAY_MS = 700;

  on(document, 'click', function (event) {
    var card = closestSel(event ? event.target : null, '[data-v-usp]');
    if (!card || !card.classList) return;
    if (reducedMotion()) return;

    if (card.__vUspTimer) window.clearTimeout(card.__vUspTimer);
    removeClass(card, 'v-usp--play');
    /* Forced reflow. Reading a layout property between the remove and the add
       is what makes a rapid second click RESTART the animation instead of
       letting the first one run on. Parked on a property, not discarded, so
       nothing downstream can treat the read as dead code. */
    card.__vUspReflow = card.offsetWidth;

    addClass(card, 'v-usp--play');
    card.__vUspTimer = window.setTimeout(function () {
      removeClass(card, 'v-usp--play');
      card.__vUspTimer = null;
    }, USP_PLAY_MS);
  });

  window.__vitelliPdp = {
    refresh: initAll,
    sweep: sweep,
    refreshCart: refreshCart
  };

  if (document.readyState === 'loading') {
    on(document, 'DOMContentLoaded', initAll);
  } else {
    initAll();
  }
}());
